/**
 * ModelSelect — editable dropdown (combobox) for AI model names.
 *
 * Renders a PrimeReact `Dropdown` with `editable`, so the user can either
 * pick a known model from the list or type a custom identifier. This replaces
 * the previous `InputText` + `<datalist>` pairing with a single widget that
 * surfaces the available models as a first-class dropdown while keeping free
 * text entry for models not in the suggestion list.
 *
 * Styling follows the shared AI Center primitives (ui.tsx) so it matches the
 * other inputs inside the config edit drawer.
 */
import type { JSX } from 'react'
import { Dropdown } from 'primereact/dropdown'
import { DROPDOWN_CLASS, DROPDOWN_PANEL_CLASS } from './ui.js'

export interface ModelSelectProps {
  /** Current model name (controlled). */
  value: string
  /** Called with the new value on every change (select or free text). */
  onChange: (value: string) => void
  /** Known model identifiers offered in the dropdown panel. */
  options: string[]
  /** Placeholder shown when empty. */
  placeholder?: string
  /** Disables editing (e.g. view mode). */
  disabled?: boolean
}

export function ModelSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: ModelSelectProps): JSX.Element {
  const dropdownOptions = options.map((model) => ({ label: model, value: model }))

  return (
    <Dropdown
      value={value}
      options={dropdownOptions}
      optionLabel="label"
      optionValue="value"
      editable
      filter
      filterBy="label"
      onChange={(e: { value: string }) => onChange(e.value)}
      placeholder={placeholder ?? 'Select or type a model'}
      disabled={disabled ?? false}
      className={DROPDOWN_CLASS}
      panelClassName={DROPDOWN_PANEL_CLASS}
    />
  )
}
